import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, User } from 'lucide-react';
import { blogPosts } from '../data/blogData';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/BlogPost.css';

// Parse inline **bold** and [text](url) markdown into React nodes
const renderInlineBold = (text) => {
  // Split on bold OR link tokens while keeping the delimiters
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const [, label, href] = linkMatch;
      const isExternal = /^https?:\/\//i.test(href);
      return (
        <a
          key={i}
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
        >
          {label}
        </a>
      );
    }
    return part;
  });
};

// Parse full content into block-level nodes: headings, tables, code fences, hr, lists, paragraphs.
const renderContent = (content) => {
  const lines = content.split('\n');
  const nodes = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const raw = lines[i];
    const trimmed = raw.trim();

    if (trimmed.startsWith('```')) {
      const lang = trimmed.slice(3).trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++;
      nodes.push(
        <pre key={`code-${key++}`} className="post-codeblock" data-lang={lang || 'text'}>
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    if (trimmed === '---') {
      i++;
      continue;
    }

    if (
      trimmed.startsWith('|') &&
      i + 1 < lines.length &&
      /^\|[\s:|-]+\|$/.test(lines[i + 1].trim())
    ) {
      const splitCells = (row) =>
        row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
      const headerCells = splitCells(raw);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(splitCells(lines[i]));
        i++;
      }
      nodes.push(
        <div key={`tbl-${key++}`} className="post-table-wrap">
          <table className="post-table">
            <thead>
              <tr>
                {headerCells.map((h, hi) => (
                  <th key={hi}>{renderInlineBold(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, ci) => (
                    <td key={ci}>{renderInlineBold(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (!trimmed) {
      i++;
      continue;
    }

    if (trimmed.startsWith('# ')) {
      nodes.push(<h1 key={`h1-${key++}`}>{trimmed.replace('# ', '')}</h1>);
      i++;
      continue;
    }
    if (trimmed.startsWith('## ')) {
      nodes.push(<h2 key={`h2-${key++}`}>{trimmed.replace('## ', '')}</h2>);
      i++;
      continue;
    }
    if (trimmed.startsWith('### ')) {
      nodes.push(<h3 key={`h3-${key++}`}>{trimmed.replace('### ', '')}</h3>);
      i++;
      continue;
    }
    if (trimmed.startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        items.push(lines[i].trim().replace(/^- /, ''));
        i++;
      }
      nodes.push(
        <ul key={`ul-${key++}`}>
          {items.map((it, ii) => (
            <li key={ii}>{renderInlineBold(it)}</li>
          ))}
        </ul>
      );
      continue;
    }
    if (trimmed.startsWith('**') && trimmed.endsWith('**') && (trimmed.match(/\*\*/g) || []).length === 2) {
      nodes.push(<p key={`strong-${key++}`}><strong>{trimmed.slice(2, -2)}</strong></p>);
      i++;
      continue;
    }
    nodes.push(<p key={`p-${key++}`}>{renderInlineBold(raw)}</p>);
    i++;
  }
  return nodes;
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.slug === slug);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Inject SEO meta tags (title + meta description + og + canonical + JSON-LD) for this blog post
  React.useEffect(() => {
    if (!post) return;
    const previousTitle = document.title;
    document.title = post.metaTitle || `${post.title} | Sanjib Roy`;

    const upsertMeta = (selector, attr, name, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      if (content) el.setAttribute('content', content);
      return { el, prev };
    };

    const description = post.metaDescription || post.excerpt || '';
    const descRes = upsertMeta('meta[name="description"]', 'name', 'description', description);
    const ogTitleRes = post.ogTitle ? upsertMeta('meta[property="og:title"]', 'property', 'og:title', post.ogTitle) : null;
    const ogDescRes = post.ogDescription ? upsertMeta('meta[property="og:description"]', 'property', 'og:description', post.ogDescription) : null;
    const ogTypeRes = upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'article');
    const ogUrlRes = post.canonical ? upsertMeta('meta[property="og:url"]', 'property', 'og:url', post.canonical) : null;

    let canonicalEl = null;
    let previousCanonicalHref = null;
    if (post.canonical) {
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
      } else {
        previousCanonicalHref = canonicalEl.getAttribute('href');
      }
      canonicalEl.setAttribute('href', post.canonical);
    }

    let schemaScript = null;
    if (post.jsonLd) {
      schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.setAttribute('data-blog-schema', post.slug);
      schemaScript.text = JSON.stringify(post.jsonLd);
      document.head.appendChild(schemaScript);
    }

    // BreadcrumbList: Home > Blog > Post
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://sanjib-roy.com/' },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://sanjib-roy.com/blog/' },
        { '@type': 'ListItem', position: 3, name: post.title, item: post.canonical || `https://sanjib-roy.com/blog/${post.slug}/` },
      ],
    };
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.setAttribute('data-blog-breadcrumb', post.slug);
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    // Remove the global homepage FAQPage schema on this interior page (restore on unmount)
    const globalFaqEl = document.getElementById('global-faq-schema');
    const globalFaqContent = globalFaqEl ? globalFaqEl.textContent : null;
    if (globalFaqEl) globalFaqEl.remove();

    return () => {
      document.title = previousTitle;
      if (descRes.el && descRes.prev !== null) descRes.el.setAttribute('content', descRes.prev);
      if (ogTitleRes && ogTitleRes.el && ogTitleRes.prev !== null) ogTitleRes.el.setAttribute('content', ogTitleRes.prev);
      if (ogDescRes && ogDescRes.el && ogDescRes.prev !== null) ogDescRes.el.setAttribute('content', ogDescRes.prev);
      if (ogTypeRes && ogTypeRes.el && ogTypeRes.prev !== null) ogTypeRes.el.setAttribute('content', ogTypeRes.prev);
      if (ogUrlRes && ogUrlRes.el && ogUrlRes.prev !== null) ogUrlRes.el.setAttribute('content', ogUrlRes.prev);
      if (canonicalEl && previousCanonicalHref !== null) canonicalEl.setAttribute('href', previousCanonicalHref);
      if (schemaScript && schemaScript.parentNode) schemaScript.parentNode.removeChild(schemaScript);
      if (breadcrumbScript && breadcrumbScript.parentNode) breadcrumbScript.parentNode.removeChild(breadcrumbScript);
      if (globalFaqContent && !document.getElementById('global-faq-schema')) {
        const restored = document.createElement('script');
        restored.type = 'application/ld+json';
        restored.id = 'global-faq-schema';
        restored.text = globalFaqContent;
        document.head.appendChild(restored);
      }
    };
  }, [post]);

  if (!post) {
    return (
      <div className="blog-post-page">
        <Header />
        <div className="post-not-found">
          <h1>Blog Post Not Found</h1>
          <Link to="/blog/" className="back-to-blog-btn">← Back to Blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const contentNodes = renderContent(post.content);

  return (
    <div className="blog-post-page">
      <style>{`
        .post-content .post-table-wrap { overflow-x: auto; margin: 24px 0; }
        .post-content .post-table { width: 100%; border-collapse: collapse; font-size: 0.95rem; }
        .post-content .post-table th, .post-content .post-table td { border: 1px solid rgba(148,163,184,0.25); padding: 10px 14px; text-align: left; vertical-align: top; }
        .post-content .post-table th { background: rgba(14,165,233,0.08); font-weight: 700; }
        .post-content .post-table tbody tr:nth-child(odd) { background: rgba(148,163,184,0.04); }
        .post-content .post-codeblock { background: #0b1220; color: #e2e8f0; padding: 18px 20px; border-radius: 10px; overflow-x: auto; font-size: 0.88rem; line-height: 1.55; margin: 24px 0; border: 1px solid rgba(148,163,184,0.15); }
        .post-content .post-codeblock code { font-family: 'Menlo', 'Monaco', 'Courier New', monospace; white-space: pre; }
        .post-content .post-divider { border: 0; border-top: 1px solid rgba(148,163,184,0.2); margin: 40px 0; }
        .post-content ul { padding-left: 22px; margin: 16px 0; }
        .post-content ul li { margin: 6px 0; line-height: 1.7; }
      `}</style>
      <Header />
      
      <article className="blog-post">
        <div className="post-header">
          <div className="post-header-container">
            <button onClick={() => navigate('/blog/')} className="back-button">
              <ArrowLeft size={20} />
              Back to Blog
            </button>
            
            <div className="post-category-badge">{post.category}</div>
            
            <h1 className="post-title">{post.title}</h1>
            
            <div className="post-meta">
              <span className="post-meta-item">
                <User size={18} />
                {post.author}
              </span>
              <span className="post-meta-item">
                <Calendar size={18} />
                {post.date}
              </span>
              <span className="post-meta-item">
                <Clock size={18} />
                {post.readTime}
              </span>
            </div>
          </div>
        </div>

        <div className="post-image-hero">
          <img src={post.image} alt={post.title} />
        </div>

        <div className="post-content-container">
          <div className="post-content">
            {contentNodes}
          </div>

          <div className="post-footer">
            <div className="post-cta">
              <h3>Ready to optimize your marketing?</h3>
              <p>Let's discuss how data-driven strategies can transform your business.</p>
              <Link to="/#contact" data-testid="blog-get-in-touch-btn" className="cta-button">Get in Touch</Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;
